import React, {useCallback, useEffect} from "react";
import {useDispatch, useSelector} from "react-redux";
import {useParams} from "react-router-dom";
import {useTranslation} from "react-i18next";
import {PublisherService} from "../../services/publisher.service";
import {toggleLoader} from "../../store/global/global-slice";
import {Grid, Tooltip} from "@mui/material";
import {PublisherBookPropositionModel} from "../../models/publisher/publisher-book-proposition.model";
import {BookDescriptionDialog} from "../../components/publisher/book-descriptions-dialog";
import {Hidden, Tab, Tabs} from "@material-ui/core";
import {PublisherLeftBookDetails} from "../../components/publisher/my-books/left-book-details";
import {StyledButton} from "../../components/ui-base/button";
import {ContentHeader} from "../../components/ui-base/content-header";
import {CustomTabPanel} from "../../components/ui-base/tab-panel";
import styled from "styled-components";
import {Colors} from "../../theme/colors";
import Chip from "@material-ui/core/Chip";
import {LangModel} from "../../models/lang.model";
import {CountryModel} from "../../models/country.model";
import {AllLanguages} from "../../data/languages";
import {RootState} from "../../store/root-reducer";
import BookStatus from "../../components/publisher/book-status";
import {ApplicationStatusEnum} from "../../models/application-status.enum";
import emptyLogo from "../../img/empty-publisher-logo.png";
import {SocialMediaIcons} from "../../components/ui-base/social-media-icons";
import {ExternalLinkTypeEnum} from "../../models/external-link-type.enum";
import {CustomLinearProgress} from "../../components/custom-linear-progress/custom-linear-progress";
import {HelpOutline} from "@material-ui/icons";
import {BaseService} from "../../services/base.service";

const PublisherBookPropositionDetails = () => {
	const [propositionData, setPropositionData] = React.useState<PublisherBookPropositionModel>(null);
	const [openDescriptionDialog, setOpenDescriptionDialog] = React.useState<boolean>(false);
	const [tabActive, setTabActive] = React.useState(0);

	const dispatch = useDispatch();
	const urlParams = useParams();
	const id: string = urlParams['id'] as string;
	const { t } = useTranslation(['publisher', 'common']);
	const languages: {language: string, code: string}[] = AllLanguages;

	const countries: CountryModel[] = useSelector(
		(state: RootState) => state.countries.countries
	);

	const updateStatus = (status: ApplicationStatusEnum) => {
		dispatch(toggleLoader(true));
		loadBook(id);
	}

	const getExternalLinkKey = (name: string) => {
		const linkTypes: string[] = Object.keys(ExternalLinkTypeEnum);

		return linkTypes.find((linkType) => (
			ExternalLinkTypeEnum[linkType] === name
		));
	}

	const getLangByCode = (code: string): LangModel => {
		return languages.find(lang => lang.code === code);
	}

	const getCountryByIso = (iso: string): CountryModel => {
		return countries.find(country => country.iso === iso);
	}

	const loadBook = useCallback(async (id: string) => {
		dispatch(toggleLoader(true));

		const tmpData = await PublisherService.getBookProposition(id);
		setPropositionData(tmpData);

		dispatch(toggleLoader(false));
	}, [dispatch]);

	useEffect(() => {
		if (id) {
			loadBook(id);
		}

	}, [id, loadBook]);

	return (
		<Grid style={{backgroundColor: '#fff', padding: '1rem'}} container direction={"row"} spacing={1}>
			{propositionData &&
                <>
                    <BookDescriptionDialog
                        open={openDescriptionDialog}
                        onClose={(value: any) => {
							setOpenDescriptionDialog(false);
						}}
                        description={propositionData.publishedbook.description}
                        summary={propositionData.publishedbook.summary}
                        fragment={propositionData.publishedbook.fragment}
                        review={propositionData.publishedbook.review}
                        tableOfContest={propositionData.publishedbook.table_of_contents}
                    />

                    <Hidden mdDown>
                        <PublisherLeftBookDetails book={propositionData.publishedbook} />
                    </Hidden>

                    <Grid item container direction={"column"} xs spacing={2}>
                        <StyledStatusContainer item container direction={"row"} justifyContent={"space-between"}>
                            <Grid item xs>
                                <StyledStatus>{t('detailsBook.status')} <span>{t(`common:applicationStatus.status${propositionData?.status.replace('Aut', 'Pub')}`)}</span></StyledStatus>
                            </Grid>

                            <Hidden smDown>
                                <Grid item xs>
									{propositionData &&
                                        <BookStatus
											status={propositionData.status}
											id={propositionData.id}
											bookmarked={propositionData.publisherto_bookmarked}
											updateStatus={updateStatus}
                                            publisherToPublisher={true}
										/>
									}
                                </Grid>
                            </Hidden>
                        </StyledStatusContainer>

                        <Grid item container direction={"row"} spacing={1}>
                            <Grid item container direction={"column"} xs={12} sm={12} md={8} spacing={2}>
								<Grid item container direction={"column"} style={{position: 'relative'}}>
									<Grid item>
										<StyledH4>{propositionData.publishedbook.original_title}</StyledH4>
									</Grid>
									<Grid item>
										<StyledH6>{propositionData.publishedbook.en_title}</StyledH6>
									</Grid>
									<Grid item>
										<StyledDate>{t('common:created')}: {new Date(propositionData.publishedbook.created).toLocaleString()}</StyledDate>
									</Grid>

									<Grid item>
										<StyledDate>{t('common:updated')}: {new Date(propositionData.publishedbook.updated).toLocaleString()}</StyledDate>
									</Grid>

								</Grid>

								<Grid item>
									<ContentHeader title={t('detailsMyBook.detailsInfo')}/>
								</Grid>

								<Grid item>
									<Grid item>
										<StyledDetailsSpan>{t('publisher:addBookNew.form.author')}:</StyledDetailsSpan>
									</Grid>

									<Grid item>
										<StyledDetailsP>{propositionData.publishedbook.authors || '---'}</StyledDetailsP>
									</Grid>
								</Grid>

								<Grid item container direction={"row"}>
									<Grid item xs={12} md={6}>
										<Grid item>
											<StyledDetailsSpan>{t('publisher:addBookNew.form.isbn')}:</StyledDetailsSpan>
										</Grid>

										<Grid item>
											<StyledDetailsP>{propositionData.publishedbook.isbn || '---'}</StyledDetailsP>
										</Grid>
									</Grid>

									<Grid item xs={12} md={6}>
										<Grid item>
											<StyledDetailsSpan>{t('publisher:addBookNew.form.originalLang')}:</StyledDetailsSpan>
										</Grid>

										<Grid item>
											<StyledDetailsP>{propositionData.publishedbook.book_language || '---'}</StyledDetailsP>
										</Grid>
									</Grid>
								</Grid>

								<Grid item>
									<StyledSpan1>{t('publisher:addBookNew.form.translatedTo')}:</StyledSpan1>
									<Grid container direction={'row'} spacing={1}>
										{propositionData.publishedbook.translations?.map((translation, index) => (
											<Grid item key={index} style={{maxWidth: '100%'}}>
												<StyledTagChip size="small" label={getLangByCode(translation.language).language} style={{maxWidth: '100%'}} />
											</Grid>
										))}
									</Grid>
								</Grid>

								<Grid item>
									<StyledSpan1>{t('publisher:addBookNew.form.countryWithLicense')}:</StyledSpan1>
									<Grid container direction={'row'} spacing={1}>
										{propositionData.publishedbook.countries_sold_to?.map((country, index) => (
											<Grid item key={index} style={{maxWidth: '100%'}}>
												<StyledTagChip size="small" label={getCountryByIso(country).name} style={{maxWidth: '100%'}}/>
											</Grid>
										))}
									</Grid>
								</Grid>

								<Grid item>
									<StyledSpan1>{t('common:categories')}:</StyledSpan1>
									<Grid container direction={'row'} spacing={1}>
										{propositionData.publishedbook.categories.map((category) => (
											<Grid item key={category.id} style={{maxWidth: '100%'}}>
												<StyledCategoryChip size="small" label={category.name} style={{maxWidth: '100%'}} />
											</Grid>
										))}
									</Grid>
								</Grid>

								<Grid item>
									<StyledSpan1>{t('common:tags')}:</StyledSpan1>
									<Grid container direction={'row'} spacing={1}>
										{propositionData.publishedbook.tags.filter((tag) => tag.tag_type === 'tag').map((tag) => (
											<Grid item key={tag.id} style={{maxWidth: '100%'}}>
												<StyledTagChip size="small" label={tag.name} style={{maxWidth: '100%'}}/>
											</Grid>
										))}
									</Grid>
								</Grid>

								<Grid item>
									<Tabs value={tabActive} onChange={(event, newValue) => { setTabActive(newValue) }}>
										<Tab label={t('common:bookDescriptionDialog.tabTitle1')} style={{fontSize: '0.9rem'}}/>
										<Tab label={t('common:bookDescriptionDialog.tabTitle2')} style={{fontSize: '0.9rem'}}/>
										<Tab label={t('common:bookDescriptionDialog.tabTitle3')} style={{fontSize: '0.9rem'}}/>
										<Tab label={t('common:bookDescriptionDialog.tabTitle4')} style={{fontSize: '0.9rem'}}/>
										<Tab label={t('common:bookDescriptionDialog.tabTitle5')} style={{fontSize: '0.9rem'}}/>
									</Tabs>

									<CustomTabPanel value={tabActive} index={0}>
										{propositionData.publishedbook.fragment &&
											<StyledDescription>{BaseService.cutText(propositionData.publishedbook.fragment, 1500)}</StyledDescription>
										}

										{!propositionData.publishedbook.fragment &&
											<StyledEmptyDescription>{t('common:bookDescriptionDialog.fillPart')}</StyledEmptyDescription>
										}

										{propositionData.publishedbook.fragment.length > 1500 &&
											<StyledButton type={"button"} style={{marginTop: '1.5rem'}} color={"transparentViolet"} onClick={() => {
												setOpenDescriptionDialog(true);
											}}>{t('common:bookDescriptionDialog.showPart')}</StyledButton>
										}
									</CustomTabPanel>

									<CustomTabPanel value={tabActive} index={1}>
										{propositionData.publishedbook.description &&
											<StyledDescription>{BaseService.cutText(propositionData.publishedbook.description, 1500)}</StyledDescription>
										}

										{!propositionData.publishedbook.description &&
											<StyledEmptyDescription>{t('common:bookDescriptionDialog.fillDesc')}</StyledEmptyDescription>
										}

										{propositionData.publishedbook.description.length > 1500 &&
											<StyledButton type={"button"} style={{marginTop: '1.5rem'}} color={"transparentViolet"} onClick={() => {
												setOpenDescriptionDialog(true);
											}}>{t('common:bookDescriptionDialog.showDesc')}</StyledButton>
										}
									</CustomTabPanel>

									<CustomTabPanel value={tabActive} index={2}>
										{propositionData.publishedbook.summary &&
											<StyledDescription>{BaseService.cutText(propositionData.publishedbook.summary, 1500)}</StyledDescription>
										}

										{!propositionData.publishedbook.summary &&
											<StyledEmptyDescription>{t('common:bookDescriptionDialog.fillSummary')}</StyledEmptyDescription>
										}

										{propositionData.publishedbook.summary.length > 1500 &&
											<StyledButton type={"button"} style={{marginTop: '1.5rem'}} color={"transparentViolet"} onClick={() => {
												setOpenDescriptionDialog(true);
											}}>{t('common:bookDescriptionDialog.showSummary')}</StyledButton>
										}
									</CustomTabPanel>

									<CustomTabPanel value={tabActive} index={3}>
										{propositionData.publishedbook.review &&
											<StyledDescription>{BaseService.cutText(propositionData.publishedbook.review, 1500)}</StyledDescription>
										}

										{!propositionData.publishedbook.review &&
											<StyledEmptyDescription>{t('common:bookDescriptionDialog.fillReview')}</StyledEmptyDescription>
										}

										{propositionData.publishedbook.review.length > 1500 &&
											<StyledButton type={"button"} style={{marginTop: '1.5rem'}} color={"transparentViolet"} onClick={() => {
												setOpenDescriptionDialog(true);
											}}>{t('common:bookDescriptionDialog.showReview')}</StyledButton>
										}
									</CustomTabPanel>

									<CustomTabPanel value={tabActive} index={4}>
										{propositionData.publishedbook.table_of_contents &&
											<StyledDescription>{BaseService.cutText(propositionData.publishedbook.table_of_contents, 1500)}</StyledDescription>
										}

										{!propositionData.publishedbook.table_of_contents &&
											<StyledEmptyDescription>{t('common:bookDescriptionDialog.fillTableOfContents')}</StyledEmptyDescription>
										}

										{propositionData.publishedbook.table_of_contents.length > 1500 &&
											<StyledButton type={"button"} style={{marginTop: '1.5rem'}} color={"transparentViolet"} onClick={() => {
												setOpenDescriptionDialog(true);
											}}>{t('common:bookDescriptionDialog.showTableOfContents')}</StyledButton>
										}
									</CustomTabPanel>
								</Grid>
							</Grid>

                            <Grid item container direction={"column"} xs={12} sm={12} md={4} spacing={1}>
								{(propositionData?.matching || propositionData?.matching >= 0) &&
                                    <Grid item container direction={"row"} alignItems={"center"} spacing={1}>
                                        <Grid item>
                                            <StyledPMatching>
												{t('detailsBook.matching')}
                                            </StyledPMatching>
                                        </Grid>

                                        <Grid item xs>
                                            <CustomLinearProgress
                                                value={BaseService.getMatchingValue(propositionData.matching)}
                                            />
                                        </Grid>

                                        <StyledPercent item>
											{/*<Tooltip title="Info"><HelpOutline style={{fontSize: '17px', marginTop: '2px'}}/></Tooltip>*/}
											{/*{Math.round((application.matching || 0) * 100)}%*/}
                                        </StyledPercent>
                                    </Grid>
								}
								{(propositionData?.market_potential || propositionData?.market_potential >= 0) &&
                                    <Grid item container direction={"row"} alignItems={"center"} spacing={1}>
                                        <Grid item>
                                            <StyledPMatching>{t('detailsBook.marketPotential')}</StyledPMatching>
                                        </Grid>

                                        <Grid item xs>
                                            <CustomLinearProgress
                                                value={propositionData.market_potential ? (propositionData.market_potential * 100 / 52) : 0}
                                            />
                                        </Grid>

                                        <StyledPercent item>
                                            <Tooltip title={t('detailsBook.marketPotentialDesc')}><HelpOutline style={{fontSize: '17px', marginTop: '2px'}}/></Tooltip>
											{/*{Math.round((application.market_potential || 0))}%*/}
                                        </StyledPercent>
                                    </Grid>
								}
                                <StyledAuthorGrid item container direction={"column"}>
                                    <Grid item container direction={"column"} alignItems={"center"} spacing={1}>
                                        <Grid item>
                                            <StyledAuthorLogo style={{backgroundImage: `url(${propositionData?.publisherfrom.logo?.file || emptyLogo})`}}/>
                                        </Grid>

                                        <Grid item>
                                            <StyledH6Nickname>{propositionData?.publisherfrom?.name}</StyledH6Nickname>
                                        </Grid>

                                        <Grid item container direction="row" spacing={1} justifyContent={"center"}>
											{propositionData?.publisherfrom?.external_links.map((externalLink) => (
												<Grid item key={externalLink.id}>
													<a href={externalLink.url} target="_blank" rel="noreferrer">
														<SocialMediaIcons iconName={getExternalLinkKey(externalLink.name)}/>
													</a>
												</Grid>
											))}
                                        </Grid>
                                    </Grid>

                                    <Grid item container direction={"column"} spacing={1} style={{padding: '0 5%'}}>

                                        <Grid item>
                                            <StyledAuthorSpan>
												{t('common:contactPerson')}:
                                            </StyledAuthorSpan>
                                            <p style={{fontSize: '0.9rem'}}>{propositionData?.publisherfrom?.details.contact_person || '-'}</p>
                                        </Grid>

                                        <Grid item>
                                            <StyledAuthorSpan>
												{t('common:email')}:
                                            </StyledAuthorSpan>
                                            <p style={{fontSize: '0.9rem'}}>{propositionData?.publisherfrom?.details.contact_email || '-'}</p>
                                        </Grid>

                                        <Grid item>
                                            <StyledAuthorSpan>
												{t('common:phone')}:
                                            </StyledAuthorSpan>
                                            <p style={{fontSize: '0.9rem'}}>{(propositionData?.publisherfrom?.details.phone_prefix + ' ' + propositionData?.publisherfrom?.details.phone) || '-'}</p>
                                        </Grid>

                                    </Grid>
                                </StyledAuthorGrid>
							</Grid>
						</Grid>
                    </Grid>
                </>
			}
		</Grid>
	);
}

const StyledStatusContainer = styled(Grid)`
  border-bottom: 1px solid #E0E0E0;
`;

const StyledStatus = styled('p')`
  font-size: 0.9rem;
  font-weight: 500;
  padding: 20px 0 15px;
  
  span {
    font-size: 0.8rem;
    padding: 2px 5px;
    border-radius: 10px;
    border: 1px solid #B3DAC7;
    background-color: #EBF5F0;
    color: ${Colors.green};
    margin-left: 5px;
  }
`;

const StyledH4 = styled('h6')`
  font-size: 1.5rem;
  color: ${Colors.violet};
  font-weight: 600;
  margin-bottom: 0.5rem;
`;

const StyledH6 = styled('h6')`
  font-size: 1rem;
  color: ${Colors.violet};
  font-weight: 500;
  margin-bottom: 0.9rem;
`;

const StyledDate = styled('p')`
  color: ${Colors.grayText};
  font-size: 0.8rem;
  font-weight: 400;
`;

const StyledEditContainer = styled('div')`
  position: absolute;
  right: 0;
`;

const StyledSpan1 = styled('span')`
  font-weight: 500;
  font-size: 0.9rem;
  color: ${Colors.darkGray};
`;

const StyledCategoryChip = styled(Chip)`
  background-color: #FBF5FF;
  color: ${Colors.lightViolet};
  border: 1px solid ${Colors.lightViolet};
  font-weight: 500;
`;

const StyledTagChip = styled(Chip)`
  background-color: #F5F5F5;
  color: #424242;
  border: 1px solid #C2C2C2;
  font-weight: 500;
`;

const StyledDescription = styled('p')`
  font-size: 0.9rem;
  white-space: pre-line;
`;

const StyledEmptyDescription = styled('p')`
  font-size: 0.9rem;
  white-space: pre-line;
  color: ${Colors.grayText};
`;

const StyledDetailsSpan = styled('span')`
  color: ${Colors.grayText};
  font-weight: 400;
  font-size: 0.9rem;
`;

const StyledDetailsP = styled('p')`
  font-weight: 400;
  font-size: 0.9rem;
`;

const StyledPMatching = styled('p')`
  font-size: 0.9rem;
  font-weight: 600;
  width: 200px;
`;

const StyledPercent = styled(Grid)`
  font-size: 1.1rem;
  font-weight: 500;
`;

const StyledAuthorGrid = styled(Grid)`
  margin-top: 2rem !important;
  background: #FBF5FF;
  border: 1px solid #E0E0E0;
  box-sizing: border-box;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.12);
  border-radius: 4px;
  padding-bottom: 2rem;
`;

const StyledAuthorLogo = styled('div')`
  height: 80px;
  width: auto;
  margin-top: -50%;
  background-repeat: no-repeat;
  background-position: center;
  background-size: cover;
`;

const StyledH6Nickname = styled('h6')`
  font-size: 1.5rem;
  color: ${Colors.violet};
  font-weight: 600;
  word-break: break-word;
  text-align: center;
`;

const StyledDot = styled('div')`
  background-color: ${Colors.grayText};
  width: 4px;
  height: 4px;
  margin: 0 5px;
  border-radius: 50%;
  align-self: center;
`;

const StyledAuthorSpan = styled('span')`
  font-weight: 500;
  font-size: 0.9rem;
  color: ${Colors.violet};
`;

export default PublisherBookPropositionDetails;
